import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Search, Save, X, Edit2 } from 'lucide-react';
import '../admin/admin_member_list.css';

const AdminApprovalList = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // 🟢 Filter choices: 'pending_proposer', 'pending_admin', 'pending_payment'
  const [activeFilter, setActiveFilter] = useState('pending_proposer');
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // const fetchApprovalMembers = async () => {
  //   setLoading(true);
  //   try {
  //     const querySnapshot = await getDocs(collection(db, "registrations"));
  //     const data = querySnapshot.docs.map(doc => ({
  //       id: doc.id,
  //       ...doc.data()
  //     }));
  //     setMembers(data);
  //   } catch (err) {
  //     console.error("Error fetching approval registry:", err);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchApprovalMembers = async () => {
  setLoading(true);
  try {
    const querySnapshot = await getDocs(collection(db, "registrations"));
    const data = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // 1. Create a lookup map for quick UID -> Member Info (Name & PArM No) resolution
    const memberLookup = {};
    data.forEach(m => {
      memberLookup[m.id] = {
        name: m.name || m.fullName || 'Unknown Member',
        parmNo: m.parmNo || ''
      };
    });

    // 2. Enrich each member record by resolving their proposerUids array
    const enrichedData = data.map(member => {
      // Assuming field name is proposerUids (or fallback to proposers)
      const uids = member.proposerUids || member.proposers || [];
      
      const resolvedProposers = uids.map(uid => {
        const info = memberLookup[uid];
        if (!info) return 'Unknown Proposer';
        return `${info.name}${info.parmNo ? ` (${info.parmNo})` : ''}`;
      });

      return {
        ...member,
        proposer1Name: resolvedProposers[0] || 'N/A',
        proposer2Name: resolvedProposers[1] || 'N/A'
      };
    });

    setMembers(enrichedData);
  } catch (err) {
    console.error("Error fetching approval registry:", err);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchApprovalMembers();
  }, []);

  // 🟢 Pipeline handling active filters & live search configurations
  useEffect(() => {
    let result = [];

    // 🟢 Handle the new filter pipeline logic
    if (activeFilter === 'pending_parm') {
      // Filter for verified members who DO NOT have a parmNo yet
      result = members.filter(member => member.status === 'verified' && !member.parmNo);
    } else {
      // Standard status filters (pending_proposer, pending_admin, pending_payment)
      result = members.filter(member => member.status === activeFilter);
    }

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      result = result.filter(member => 
        member.fullName?.toLowerCase().includes(query) ||
        member.email?.toLowerCase().includes(query) ||
        member.parmNo?.toLowerCase().includes(query)
      );
    }

    setFilteredMembers(result);
  }, [searchQuery, activeFilter, members]);

  // Inline Status Modification Controls
  const handleEditClick = (member) => {
    setEditingId(member.id);
    setEditFormData({
      status: member.status || '',
      parmNo: member.parmNo || ''
    });
  };

//   const handleSaveClick = async (id) => {
//     try {
//       const docRef = doc(db, "registrations", id);
//       // Send ONLY the updated status to Firebase
//       await updateDoc(docRef, { status: editFormData.status });
      
//       setMembers(members.map(m => m.id === id ? { ...m, status: editFormData.status } : m));
//       setEditingId(null);
//     } catch (err) {
//       console.error("Failed to commit stage elevation status:", err);
//       alert("Error processing verification adjustment.");
//     }
//   };

  const handleSaveClick = async (id) => {
    try {
      const docRef = doc(db, "registrations", id);
      let payload = {};

      // 🟢 Build specific update payload based on current active workflow tab
      if (activeFilter === 'pending_parm') {
        payload = { parmNo: editFormData.parmNo };
      } else if (activeFilter === 'pending_admin') {
        payload = { status: editFormData.status };
      }

      await updateDoc(docRef, payload);
      
      // Update local state smoothly
      setMembers(members.map(m => m.id === id ? { ...m, ...payload } : m));
      setEditingId(null);
    } catch (err) {
      console.error("Failed to update member field:", err);
      alert("Error processing updates.");
    }
  };

  // Helper to convert timestamp values inside columns beautifully
  const formatTimestamp = (timestamp) => {
    if (!timestamp || !timestamp.seconds) return 'N/A';
    const date = new Date(timestamp.seconds * 1000);
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  };

  if (loading) return <div className="admin-loader">Loading pending queue items...</div>;

  const showActionColumn = activeFilter === 'pending_admin' || activeFilter === 'pending_parm';

  return (
    <div className="admin-member-page">
      <div className="admin-member-container">
        <header className="page-header">
          <h2>Membership Verification</h2>
          <p>Review and authorize applicant profiles changing stages across endorsement states.</p>
        </header>

        {/* SEARCH & PIPELINE FILTER CONTROLS */}
        <div className="toolbar-wrapper">
          <div className="search-bar-box">
            <Search size={18} className="search-icon" />
            <input 
              type="text" 
              placeholder="Search by applicant name, email..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="filter-buttons-group">
            <button 
              className={`filter-btn ${activeFilter === 'pending_proposer' ? 'active' : ''}`}
              onClick={() => { setActiveFilter('pending_proposer'); setEditingId(null); }}
            >
              Pending Proposer
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'pending_admin' ? 'active' : ''}`}
              onClick={() => { setActiveFilter('pending_admin'); setEditingId(null); }}
            >
              Pending Admin
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'pending_payment' ? 'active' : ''}`}
              onClick={() => { setActiveFilter('pending_payment'); setEditingId(null); }}
            >
              Pending Payment
            </button>

            <button 
              className={`filter-btn ${activeFilter === 'pending_parm' ? 'active' : ''}`}
              onClick={() => { setActiveFilter('pending_parm'); setEditingId(null); }}
            >
              Pending PArM No
            </button>
          </div>
        </div>

        {/* PIPELINE DATA DATAGRID */}
        <div className="table-responsive-wrapper">
          <table className="member-data-table">
            <thead>
              <tr>
                <th>PArM No</th>
                <th>CA No</th>
                <th>Full Name</th>
                <th>IC No</th>
                <th>Email</th>
                <th>Phone No</th>
                <th>Membership Type</th>
                <th>Proposer 1</th>
                <th>Proposer 2</th>
                <th>Joined Date</th>
                <th>Expiry Date</th>
                <th>Current Status</th>
                {/* 🟢 Action header column renders ONLY when filtering on pending_admin stage */}
                {showActionColumn && <th style={{ textAlign: 'center' }}>Action</th>}
              </tr>
            </thead>
            <tbody>
              {filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan={showActionColumn ? 11 : 10} className="empty-table-row">
                    No registry profiles matching this criteria queue found.
                  </td>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <tr key={member.id} className={editingId === member.id ? "row-editing" : ""}>
                    
                    {/* DYNAMIC PARM NO COLUMN */}
                    <td>
                      {editingId === member.id && activeFilter === 'pending_parm' ? (
                        <input 
                          type="text" 
                          className="table-inline-input" 
                          value={editFormData.parmNo} 
                          onChange={(e) => setEditFormData({ ...editFormData, parmNo: e.target.value })}
                          placeholder="Assign PArM No..."
                        />
                      ) : (
                        <span className="badge-parm-no">{member.parmNo || 'N/A'}</span>
                      )}
                    </td>

                    <td>{member.caNo || 'N/A'}</td>
                    
                    {/* Read-only Full Name */}
                    <td className="text-bold">{member.name}</td>

                    <td>{member.icNo || 'N/A'}</td>

                    <td>{member.email || 'N/A'}</td>

                    <td>{member.phone || 'N/A'}</td>
                    
                    {/* Read-only Type */}
                    <td>{member.memberType || 'N/A'}</td>

                    <td>
                      <span style={{ fontSize: '0.85rem', color: '#334155' }}>
                        {member.proposer1Name || member.proposer1 || 'N/A'}
                      </span>
                    </td>

                    {/* 🟢 PROPOSER 2 COLUMN */}
                    <td>
                      <span style={{ fontSize: '0.85rem', color: '#334155' }}>
                        {member.proposer2Name || member.proposer2 || 'N/A'}
                      </span>
                    </td>
                    
                    {/* Read-only Joined Date */}
                    <td><span className="date-text-node">{formatTimestamp(member.joinedDate)}</span></td>
                    
                    {/* Read-only Expiry Date */}
                    <td><span className="date-text-node">{formatTimestamp(member.membershipExpiry)}</span></td>

                    {/* DYNAMIC STATUS COLUMN */}
                    <td>
                      {editingId === member.id && activeFilter === 'pending_admin' ? (
                        <select 
                          className="table-inline-select" 
                          value={editFormData.status === "pending_payment" ? "approve" : editFormData.status === "rejected" ? "reject" : ""} 
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "approve") setEditFormData({ ...editFormData, status: "pending_payment" });
                            if (val === "reject") setEditFormData({ ...editFormData, status: "rejected" });
                          }}
                        >
                          <option value="" disabled>Select Action</option>
                          <option value="approve">Approve</option>
                          <option value="reject">Reject</option>
                        </select>
                      ) : (
                        <span 
                          className={`table-status-pill ${member.status === 'verified' ? 'status-active' : 'status-inactive'}`}
                          style={member.status === 'pending_admin' ? { backgroundColor: '#fef3c7', color: '#d97706' } : undefined}
                        >
                          {member.status === 'pending_admin' ? 'Pending Admin' : 
                           member.status === 'pending_payment' ? 'Pending Payment' : 
                           member.status === 'verified' ? 'Verified' : member.status}
                        </span>
                      )}
                    </td>

                    {/* ACTIONS INTERACTION HUB (ONLY FOR PENDING_ADMIN TAB) */}
                    {showActionColumn && (
                      <td style={{ textAlign: 'center' }}>
                        {editingId === member.id ? (
                          <div className="table-action-buttons">
                            <button className="act-btn btn-save" onClick={() => handleSaveClick(member.id)}><Save size={14} /></button>
                            <button className="act-btn btn-cancel" onClick={() => setEditingId(null)}><X size={14} /></button>
                          </div>
                        ) : (
                          <button className="act-btn btn-edit" onClick={() => handleEditClick(member)}><Edit2 size={14} /></button>
                        )}
                      </td>
                    )}

                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminApprovalList;